'use client';

import Image from 'next/image';
import { useTranslations } from 'next-intl';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useDomainConfiguration } from '@/components/DomainConfigurationProvider';

import { useFormat } from '@cineamo/ui/src/hooks/formatter/useFormat';

import TicketShape from '@cineamo/ui/src/ticket-shape/TicketShape';

import type { TourStop } from '../page';

type TourStopCardProps = {
    stop: TourStop;
};

const TourStopCard: React.FC<TourStopCardProps> = ({ stop }) => {
    const t = useTranslations();
    const { domainConfiguration } = useDomainConfiguration();
    const {
        dateTime: { formatDate, formatDate_HHmm24 }
    } = useFormat();

    const formatDateTime = useCallback(
        (dateString: string | null) => {
            if (!dateString) return '';
            try {
                const time = formatDate_HHmm24(dateString);
                const date = formatDate(dateString, { year: undefined });
                if (time === '00:00') {
                    return date;
                }
                return `${date}, ${time}`;
            } catch {
                return '';
            }
        },
        [formatDate, formatDate_HHmm24]
    );

    const cityName = stop.placeName || stop._embedded?.city?.name || '';
    const cinemaName = stop.cinema?.name || stop._embedded?.cinema?.name || '';
    const formattedDate = formatDateTime(stop.startDatetime);
    const hasTicketLink = !!stop.bookingUrl;

    return (
        <div
            className="rounded-16 p-24 flex flex-col gap-16 w-full"
            style={{
                background: 'linear-gradient(108.86deg, rgba(242, 242, 242, 0.15) 0%, rgba(242, 242, 242, 0.05) 100%)'
            }}>
            {/* City Name */}
            {cityName && (
                <h3
                    className="text-[20px] font-bold text-white text-center tracking-[1px]"
                    style={{ letterSpacing: '1px' }}>
                    {cityName.toUpperCase()}
                </h3>
            )}

            {/* Date and Cinema */}
            {(formattedDate || cinemaName) && (
                <div className="flex items-center justify-center gap-8 text-[16px] font-bold text-white flex-wrap">
                    {formattedDate && <span>{formattedDate}</span>}
                    {formattedDate && cinemaName && <span className="text-white/50">|</span>}
                    {cinemaName && <span>{cinemaName}</span>}
                </div>
            )}

            {/* What used to be 'guest' became more generic description field so we render it as is */}
            {stop.guest && <p className="text-[14px] font-medium text-white text-center">{stop.guest}</p>}

            {/* Ticket Button */}
            <div className="flex justify-center mt-8">
                {hasTicketLink ? (
                    <a
                        href={stop.bookingUrl ?? undefined}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-block transition-opacity hover:opacity-90">
                        <TicketShape
                            config={{
                                fillColorHex: domainConfiguration.style?.primaryColor || '#00A8FF',
                                fillColorOpacity: 1,
                                cornerRadius: 8
                            }}>
                            <div
                                className="relative z-10 flex items-center justify-center pl-16 pr-[20px] py-10 text-body-bold-16"
                                style={{ color: domainConfiguration.style?.onPrimaryColor || '#FFFFFF' }}>
                                {t('cinema-tour.tickets')}
                            </div>
                        </TicketShape>
                    </a>
                ) : (
                    <div className="opacity-50 cursor-not-allowed">
                        <TicketShape
                            config={{
                                fillColorHex: domainConfiguration.style?.primaryColor || '#00A8FF',
                                fillColorOpacity: 1,
                                cornerRadius: 8
                            }}>
                            <div
                                className="relative z-10 flex items-center justify-center pl-16 pr-[20px] py-10 text-body-bold-16"
                                style={{ color: domainConfiguration.style?.onPrimaryColor || '#FFFFFF' }}>
                                {t('cinema-tour.tickets-soon')}
                            </div>
                        </TicketShape>
                    </div>
                )}
            </div>
        </div>
    );
};

type CinemaTourSectionProps = {
    stops: TourStop[];
};

const CinemaTourSection: React.FC<CinemaTourSectionProps> = ({ stops }) => {
    const t = useTranslations();
    const [sortBy, setSortBy] = useState<'position' | 'date'>('position');
    const [isSortOpen, setIsSortOpen] = useState(false);
    const sortRef = useRef<HTMLDivElement>(null);

    const sortOptions: { value: 'position' | 'date'; label: string }[] = [
        { value: 'position', label: t('cinema-tour.sort-default') },
        { value: 'date', label: t('cinema-tour.sort-date') }
    ];

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (sortRef.current && !sortRef.current.contains(e.target as Node)) {
                setIsSortOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const sortedStops = useMemo(() => {
        if (!Array.isArray(stops)) return [];
        return [...stops].sort((a, b) => {
            if (sortBy === 'position') {
                return a.position - b.position;
            }
            // sort by date
            if (!a.startDatetime && !b.startDatetime) return 0;
            if (!a.startDatetime) return 1;
            if (!b.startDatetime) return -1;
            return new Date(a.startDatetime).getTime() - new Date(b.startDatetime).getTime();
        });
    }, [stops, sortBy]);

    return (
        <section
            className="py-24"
            id="kinotour">
            {/* Header */}
            <div className="flex items-center justify-between mb-32">
                <div className="flex items-center gap-12">
                    <h2 className="text-headline-20 lg:text-headline-24 tracking-[0px]">{t('cinema-tour.title')}</h2>
                    <span className="opacity-50 text-headline-20 lg:text-headline-24">•</span>
                    <span className="text-headline-20 lg:text-headline-24 opacity-50">
                        {t('cinema-tour.stops-count', { count: sortedStops.length })}
                    </span>
                </div>

                {/* Sort dropdown */}
                <div
                    ref={sortRef}
                    className="relative">
                    {/* Desktop trigger */}
                    <div className="hidden lg:flex items-center gap-8">
                        <span className="text-[14px] text-white/70">{t('cinema-tour.sort-by')}</span>
                        <button
                            onClick={() => setIsSortOpen((prev) => !prev)}
                            className="flex items-center gap-8 bg-white/10 rounded-8 px-12 py-6 cursor-pointer transition-colors hover:bg-white/15">
                            <span className="text-[14px] text-white font-bold">
                                {sortOptions.find((o) => o.value === sortBy)?.label}
                            </span>
                            <svg
                                width="10"
                                height="6"
                                viewBox="0 0 10 6"
                                fill="none"
                                className={`transition-transform ${isSortOpen ? 'rotate-180' : ''}`}>
                                <path
                                    d="M1 1l4 4 4-4"
                                    stroke="white"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </button>
                    </div>

                    {/* Mobile trigger */}
                    <button
                        onClick={() => setIsSortOpen((prev) => !prev)}
                        className="lg:hidden flex items-center justify-center w-32 h-32 cursor-pointer">
                        <svg
                            width="20"
                            height="16"
                            viewBox="0 0 20 16"
                            fill="none">
                            <path
                                d="M0 1h20M0 8h14M0 15h8"
                                stroke="white"
                                strokeWidth="2"
                                strokeLinecap="round"
                            />
                        </svg>
                    </button>

                    {/* Dropdown panel */}
                    {isSortOpen && (
                        <div className="absolute z-50 right-0 mt-8 rounded-8 bg-white overflow-hidden shadow-lg min-w-[140px]">
                            {sortOptions.map((option, index) => (
                                <button
                                    key={option.value}
                                    onClick={() => {
                                        setSortBy(option.value);
                                        setIsSortOpen(false);
                                    }}
                                    className={`flex items-center gap-8 w-full px-16 py-10 text-[14px] text-left cursor-pointer transition-colors hover:bg-gray-100 ${
                                        index < sortOptions.length - 1 ? 'border-b border-gray' : ''
                                    }`}>
                                    <span className="w-16">
                                        {sortBy === option.value && (
                                            <svg
                                                width="12"
                                                height="10"
                                                viewBox="0 0 12 10"
                                                fill="none">
                                                <path
                                                    d="M1 5l3.5 3.5L11 1"
                                                    stroke="#1a1a2e"
                                                    strokeWidth="2"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        )}
                                    </span>
                                    <span
                                        className={`text-darkBlue ${sortBy === option.value ? 'font-bold' : 'font-normal'}`}>
                                        {option.label}
                                    </span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Timeline Grid - Desktop: each stop is one grid row so logo and card are always aligned */}
            <div className="hidden lg:grid lg:grid-cols-[1fr_80px_1fr]">
                {sortedStops.map((stop, index) => {
                    const isLeft = index % 2 === 0;
                    return (
                        <React.Fragment key={stop.id}>
                            {/* Left cell */}
                            <div className="flex items-center justify-end py-16">
                                {isLeft && <TourStopCard stop={stop} />}
                            </div>

                            {/* Center cell - Logo with timeline lines that stretch to fill row height */}
                            <div className="flex flex-col items-center">
                                {index > 0 ? (
                                    <div className="w-[2px] flex-1 border-l-2 border-dashed border-white/20" />
                                ) : (
                                    <div className="flex-1" />
                                )}
                                <div className="relative z-10 w-48 h-48 shrink-0 rounded-full bg-white/10 border-2 border-white/30 flex items-center justify-center overflow-hidden">
                                    {stop.cinema?.logoImageUrl || stop.cinema?.profileImageUrl ? (
                                        <Image
                                            src={stop.cinema.logoImageUrl || stop.cinema.profileImageUrl || ''}
                                            alt={stop.cinema.name}
                                            width={48}
                                            height={48}
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <div className="w-24 h-24 rounded-full bg-white/20" />
                                    )}
                                </div>
                                {index < sortedStops.length - 1 ? (
                                    <div className="w-[2px] flex-1 border-l-2 border-dashed border-white/20" />
                                ) : (
                                    <div className="flex-1" />
                                )}
                            </div>

                            {/* Right cell */}
                            <div className="flex items-center py-16">{!isLeft && <TourStopCard stop={stop} />}</div>
                        </React.Fragment>
                    );
                })}
            </div>

            {/* Timeline Grid - Mobile: each stop is one grid row so logo and card are always aligned */}
            <div className="lg:hidden grid grid-cols-[48px_1fr] gap-x-16">
                {sortedStops.map((stop, index) => (
                    <React.Fragment key={stop.id}>
                        {/* Logo column */}
                        <div className="flex flex-col items-center">
                            {index > 0 ? (
                                <div className="w-[2px] flex-1 border-l-2 border-dashed border-white/20" />
                            ) : (
                                <div className="flex-1" />
                            )}
                            <div className="relative z-10 w-48 h-48 shrink-0 rounded-full bg-white/10 border-2 border-white/30 flex items-center justify-center overflow-hidden">
                                {stop.cinema?.logoImageUrl ? (
                                    // eslint-disable-next-line @next/next/no-img-element
                                    <img
                                        src={stop.cinema.logoImageUrl}
                                        alt={stop.cinema.name}
                                        className="w-full h-full object-cover"
                                    />
                                ) : (
                                    <div className="w-24 h-24 rounded-full bg-white/20" />
                                )}
                            </div>
                            {index < sortedStops.length - 1 ? (
                                <div className="w-[2px] flex-1 border-l-2 border-dashed border-white/20" />
                            ) : (
                                <div className="flex-1" />
                            )}
                        </div>

                        {/* Card column */}
                        <div className="py-12">
                            <TourStopCard stop={stop} />
                        </div>
                    </React.Fragment>
                ))}
            </div>
        </section>
    );
};

export default CinemaTourSection;
